import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { renderTooltipIcon } from 'components/TableauObs/common/KpiCard';

interface TrendIncrementData {
  increment2026: number;
  sparklineData: number[];
}

interface Props {
  data: TrendIncrementData | null;
  isLoading: boolean;
  year?: number;
  color?: string;
  bgColor?: string;
  valueFontSize?: any;
  valueFontWeight?: any;
}

const formatNumber = (value: number | undefined): { integer: string; decimal: string } => {
  if (value === undefined || value === null || isNaN(value)) {
    return { integer: '0', decimal: '00' };
  }
  const [integer, decimal] = value.toFixed(2).split('.');
  return { integer, decimal };
};

const TrendIncrementWidget: React.FC<Props> = ({
  data,
  isLoading,
  color = "#000000",
  bgColor = 'primary.main',
  year = new Date().getFullYear() + 1,
  valueFontSize,
  valueFontWeight
}) => {
  const theme = useTheme();

  const incrementValue = data?.increment2026 || 0;
  const isZero = incrementValue === 0;
  const isPositiveGrowth = incrementValue > 0;

  // Extract nested ternary operation
  let growthColor = theme.palette.text.main;
  if (!isZero) {
    growthColor = isPositiveGrowth ? '#01C008' : '#D82B51';
  }

  const TrendIcon = isPositiveGrowth ? TrendingUpIcon : TrendingDownIcon;
  const formattedNumber = formatNumber(Math.abs(incrementValue));

  const sparklineChartData = (data?.sparklineData || []).map((value, index) => ({
    index,
    value
  }));

  const tooltipContent = `${year} Trend Line Increment

Value represents projected cost increase based on historical spending trends
Data source: Finance Excel worksheet`;

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        p={2}
        bgcolor={bgColor}
        color={color}
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        sx={{
          position: 'relative',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}
      >
        <Stack direction="row" gap={2} alignItems="start" minHeight="38px">
          <Typography variant="widgetTitle">
            <HighlightedText text={`Trend Line Increment ${year}`} />
          </Typography>
          <Box
            className="hover-content"
            sx={{
              opacity: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            {renderTooltipIcon(
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>{tooltipContent}</pre>,
              'light'
            )}
          </Box>
        </Stack>

        <Stack minHeight="43px" mt={1.5} flexGrow={1} justifyContent="center">
          {!isLoading ? (
            data?.increment2026 == null ? (
              <Typography variant="bodyText" sx={{ color: 'textLight.secondary' }}>Data not available</Typography>
            ) : (
              <Stack color="text.main" direction="column" alignItems="start" minWidth={0}>
                <Stack direction="row" alignItems="baseline" flexWrap="wrap" minWidth={0} maxWidth="100%">
                  <Typography
                    variant="widgetValue"
                    sx={{
                      color: growthColor,
                      lineHeight: 1,
                      fontSize: valueFontSize ?? { xs: '38px', sm: '50px', md: '60px' },
                      ...(valueFontWeight && { fontWeight: valueFontWeight })
                    }}
                  >
                    {!isZero && isPositiveGrowth ? '+' : ''}${formattedNumber.integer}
                  </Typography>
                  {!isZero && (
                    <Typography
                      variant="widgetUnit"
                      sx={{
                        color: growthColor,
                        fontSize: valueFontSize ?? { xs: '14px', sm: '20px', md: '26px' },
                        ...(valueFontWeight && { fontWeight: valueFontWeight }),
                        m: 0
                      }}
                    >
                      .{formattedNumber.decimal}
                    </Typography>
                  )}
                  <Typography
                    variant="widgetUnit"
                    sx={{
                      color: growthColor,
                      fontSize: valueFontSize ?? { xs: '14px', sm: '24px', md: '32px' },
                      ...(valueFontWeight && { fontWeight: valueFontWeight })
                    }}
                  >
                    MM
                  </Typography>
                  {!isZero && <TrendIcon sx={{ ml: 1, fontSize: '1.5rem', color: growthColor }} />}
                </Stack>

                <Box sx={{ height: 20, width: '100%', mt: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparklineChartData}>
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={theme.palette.info?.main || '#4fc3f7'}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>
            )
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default TrendIncrementWidget;
